/**
 * What a page renders when it has *no* data, and the two callouts that ride
 * alongside real numbers when the snapshot is only partly trustworthy.
 *
 * This board's expected state is `ok` — the credential already reads the whole
 * tenant — so unlike the Defender board this component is the exception rather
 * than the norm. It still has to be exactly right, because the failure it guards
 * against is the worst one available here: a 403 rendered as `0 AI accounts`,
 * `0 exhausted quotas` and `0 throttled calls` would report a smaller, cheaper,
 * better-governed estate than the real one and give no hint that it was doing so
 * (§5 "unconfigured ≠ empty ≠ zero", §8 "never let the shortfall read as a
 * healthy zero"). So when the state is not `ok` the panels below do not render at
 * all: no strip of zeros, no empty axis, no flat line along the bottom.
 *
 * Three components, three different jobs:
 *
 * - `AiAccessPanel` — the page has nothing. Names the state, whose problem it is,
 *   and what to do.
 * - `AiErrorsAlert` — the page has numbers *and* `errors[]`. The numbers are a
 *   floor and the alert sits next to the figures it undermines. Renders nothing
 *   when the list is empty.
 * - `AiCoverageAlert` — the coverage itself is a finding: a truncated query, a
 *   resource that disappeared, a resource that appeared.
 */

import { Icon } from "@norican/icons";
import { Alert, Badge, Card, DescriptionList, Typography } from "@norican/react";

import {
  ACCESS_COPY,
  type AiPanelState,
  coverageCaption,
  coverageIsNoteworthy,
  coverageTone,
  formatCount,
  formatWindow,
  LOWER_BOUND_ICON,
  NOT_ZERO_NOTE,
} from "./status";

interface AiAccessPanelProps {
  /** What this page would have shown, e.g. "Quota headroom". */
  panel: string;
  /** The payload's state contract. */
  state: AiPanelState;
  /**
   * Collapse to a single line, because the same state has already been explained
   * in full higher up the page. Four of the five routes are projections of one
   * cached build, so the same cause repeated four times would read as four
   * separate faults.
   */
  repeated?: boolean;
}

export function AiAccessPanel({ panel, state, repeated = false }: AiAccessPanelProps) {
  const copy = ACCESS_COPY[state.access_state];

  if (repeated) {
    return (
      <Alert
        tone={copy.tone}
        icon={<Icon icon={copy.icon} size="sm" />}
        title={`${panel} — ${copy.badge}`}
      >
        <Typography variant="body-sm" tone="muted">
          Same cause as above. Nothing was measured, so nothing is shown — not zero, and not an
          empty chart.
        </Typography>
      </Alert>
    );
  }

  const facts = [
    { label: "Panel", value: panel },
    { label: "Window it would cover", value: formatWindow(state.window) },
    {
      label: "Credential",
      value: state.configured
        ? "Present — NOVA authenticated successfully"
        : "Absent — nothing was asked of Azure",
    },
    {
      label: "Subscriptions that answered",
      value: formatCount(state.coverage.subscriptions_visible),
    },
    {
      label: "Accounts the registry knows",
      value: formatCount(state.coverage.accounts_known),
    },
  ];

  return (
    <Card variant="outline" accent="left" accentTone={copy.tone}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <Icon icon={copy.icon} size="md" />
            <Typography variant="heading-4" as="h2">
              {copy.title}
            </Typography>
          </div>
          {/* State is never colour-alone (§3.2) — the badge names it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          {/* The backend's own one-liner, verbatim. It already names the role,
              the scope and the subscription — paraphrasing it would lose the
              exact strings an operator has to paste somewhere. */}
          <Alert tone={copy.tone} title={`Azure: what happened to "${panel}"`}>
            {state.status_detail}
          </Alert>

          <Typography variant="body-sm" tone="muted">
            {copy.owner}
          </Typography>

          {copy.remedy.length > 0 && (
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              {copy.remedy.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          )}

          <DescriptionList items={facts} columns={2} />

          <Typography variant="body-sm" tone="muted">
            {NOT_ZERO_NOTE}
          </Typography>

          {state.errors.length > 0 && <AiErrorsAlert errors={state.errors} />}
        </div>
      </Card.Body>
    </Card>
  );
}

interface AiErrorsAlertProps {
  /** The payload's `errors[]` — non-fatal problems hit while collecting. */
  errors: readonly string[];
  /**
   * Heading override. The default suits the partial case (`access_state: "ok"`
   * with some queries failed), which is the one that needs the loudest label.
   */
  title?: string;
}

/**
 * `errors[]` surfaced as an `Alert` (§5), never swallowed — and rendered right
 * next to the figures it undermines rather than at the bottom of the page.
 *
 * Returns `null` for an empty list rather than an empty box, so a clean snapshot
 * carries no residue. The backend already folds identical causes into one line
 * and names the affected queries, so the list is rendered as-is rather than
 * deduplicated again.
 *
 * The title states the consequence, not the fact: when this appears beside real
 * numbers, **those numbers are a floor**.
 */
export function AiErrorsAlert({ errors, title }: AiErrorsAlertProps) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <Alert
      tone="warning"
      icon={<Icon icon={LOWER_BOUND_ICON} size="sm" />}
      title={title ?? "This snapshot is incomplete — every figure below is a floor, not a census"}
    >
      <ul className="flex flex-col gap-1">
        {errors.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </Alert>
  );
}

interface AiCoverageAlertProps {
  /** The payload's own coverage block. */
  coverage: AiPanelState["coverage"];
  /**
   * Render even when nothing about the coverage is noteworthy.
   *
   * Off by default: on a clean read the coverage line is a caption under the KPI
   * strip, not a callout. The overview turns it on so the scope of every total is
   * always stated somewhere on the page.
   */
  always?: boolean;
}

/**
 * The coverage block as a callout when it is itself a finding.
 *
 * Three things make it one, and they need opposite responses, so the tone
 * distinguishes them: a resource the registry has seen has **disappeared**
 * (danger — deleted, or no longer visible, and NOVA cannot tell which); a query
 * was **truncated** or a subscription was unreadable (warning — every count is a
 * floor); a resource **appeared** (information — a finding management wants, and
 * how shadow provisioning surfaces).
 */
export function AiCoverageAlert({ coverage, always = false }: AiCoverageAlertProps) {
  const noteworthy = coverageIsNoteworthy(coverage);
  if (!noteworthy && !always) {
    return null;
  }
  const tone = noteworthy ? coverageTone(coverage) : "neutral";
  return (
    <Alert
      tone={tone === "neutral" ? "neutral" : tone}
      icon={noteworthy ? <Icon icon={LOWER_BOUND_ICON} size="sm" /> : undefined}
      title={
        coverage.accounts_missing > 0
          ? "A resource the registry has seen is no longer being returned"
          : coverage.is_lower_bound
            ? "Every count on this page is a lower bound"
            : "What these numbers cover"
      }
    >
      {coverageCaption(coverage)}
    </Alert>
  );
}
